import { ActionIcon, Alert, Center, Loader, Menu, SimpleGrid, Stack, Text, TextInput } from '@mantine/core';
import { Search, CircleAlert, ArrowUp, ArrowDown, ChevronsUpDown } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTickers, type TickerRow } from '@/hooks/useTickers';
import { useUiStore, type SortCol, type SortDir } from '@/lib/ui-store';
import { StockCard } from './StockCard';
import { StockTable } from './StockTable';
import { TickerDetailModal } from './TickerDetailModal';

const SORT_LABELS: Record<SortCol, string> = {
  symbol: 'Symbol',
  name: 'Company',
  last_price: 'Price',
  last_price_change_pct: 'Change %',
  market_cap: 'Market cap',
  buzz_score: 'Buzz Score',
};

function SortMenu({ sortCol, sortDir, setSort }: { sortCol: SortCol; sortDir: SortDir; setSort: (col: SortCol) => void }) {
  return (
    <Menu shadow="md" width={180} position="bottom-end">
      <Menu.Target>
        <ActionIcon
          variant="subtle"
          size={44}
          aria-label={`Sort by ${SORT_LABELS[sortCol]} ${sortDir === 'asc' ? 'ascending' : 'descending'}`}
        >
          <ChevronsUpDown size={22} aria-hidden />
        </ActionIcon>
      </Menu.Target>
      <Menu.Dropdown>
        {(Object.entries(SORT_LABELS) as [SortCol, string][]).map(([col, label]) => (
          <Menu.Item
            key={col}
            onClick={() => setSort(col)}
            fw={sortCol === col ? 700 : undefined}
            rightSection={
              sortCol === col
                ? sortDir === 'asc'
                  ? <ArrowUp size={12} aria-hidden />
                  : <ArrowDown size={12} aria-hidden />
                : undefined
            }
          >
            {label}
          </Menu.Item>
        ))}
      </Menu.Dropdown>
    </Menu>
  );
}

function applySearchAndSort(
  tickers: TickerRow[],
  search: string,
  sortCol: SortCol,
  sortDir: SortDir,
): TickerRow[] {
  const q = search.trim().toLowerCase();
  const filtered = q
    ? tickers.filter(
        (t) =>
          t.symbol.toLowerCase().includes(q) ||
          (t.name ?? '').toLowerCase().includes(q),
      )
    : tickers;

  return [...filtered].sort((a, b) => {
    const aVal = a[sortCol];
    const bVal = b[sortCol];
    if (aVal === null && bVal === null) return 0;
    if (aVal === null) return 1; // nulls last regardless of direction
    if (bVal === null) return -1;
    const cmp =
      typeof aVal === 'string' && typeof bVal === 'string'
        ? aVal.localeCompare(bVal)
        : (aVal as number) - (bVal as number);
    return sortDir === 'asc' ? cmp : -cmp;
  });
}

export function StockList() {
  const { data, isLoading, isError, error } = useTickers();
  const { search, sortCol, sortDir, setSearch, setSort } = useUiStore();
  const [selectedTicker, setSelectedTicker] = useState<TickerRow | null>(null);

  const visible = useMemo(
    () => applySearchAndSort(data ?? [], search, sortCol, sortDir),
    [data, search, sortCol, sortDir],
  );

  if (isLoading) {
    return (
      <Center mih={240}>
        <Loader />
      </Center>
    );
  }

  if (isError) {
    return (
      <Alert color="red" icon={<CircleAlert size={18} aria-hidden />} title="Couldn't load tickers">
        {error instanceof Error ? error.message : 'Unknown error'}
      </Alert>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Stack gap="xs">
        <Text fw={500}>No tickers yet.</Text>
        <Text c="dimmed" size="sm">
          Run the refresh-tickers Worker locally to seed the database. See the README's "Running the
          Workers" section.
        </Text>
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      {/* Mobile: search + sort icon in one row */}
      <div className="flex sm:hidden items-center gap-2">
        <TextInput
          placeholder="Search…"
          leftSection={<Search size={16} aria-hidden />}
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          aria-label="Search tickers"
          style={{ flex: 1 }}
        />
        <SortMenu sortCol={sortCol} sortDir={sortDir} setSort={setSort} />
      </div>

      {/* Desktop: search only — table headers handle sorting */}
      <div className="hidden sm:block">
        <TextInput
          placeholder="Search by symbol or company name…"
          leftSection={<Search size={16} aria-hidden />}
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          aria-label="Search tickers"
          style={{ maxWidth: 360 }}
        />
      </div>

      {visible.length === 0 ? (
        <Text c="dimmed" size="sm">
          No tickers match &ldquo;{search}&rdquo;.
        </Text>
      ) : (
        <>
          <div className="hidden sm:block">
            <StockTable tickers={visible} sortCol={sortCol} sortDir={sortDir} onSort={setSort} onSelect={setSelectedTicker} />
          </div>
          <div className="block sm:hidden">
            <SimpleGrid cols={1} spacing="sm">
              {visible.map((ticker) => (
                <StockCard key={ticker.id} ticker={ticker} onSelect={setSelectedTicker} />
              ))}
            </SimpleGrid>
          </div>
          <TickerDetailModal ticker={selectedTicker} onClose={() => setSelectedTicker(null)} />
        </>
      )}
    </Stack>
  );
}
